import { useIntl } from '@tink/core/src/components/providers/LanguageProvider/useIntl';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ProviderViewSet } from '../../../utilities/provider-view-set';
import {
  getProviderSortingStrategy,
  limitNumberOfProviders,
  searchProviders,
} from '../../../utilities/provider-utils';
import { Header } from '../../../components/organisms/Header/Header';
import { ScreenWrapper } from '../../../components/atoms/ScreenWrapper/ScreenWrapper';
import { ContentBody } from '../../../components/atoms/ContentBody/ContentBody';
import { PageView } from '../../../models/analytics';
import { LegalDisclaimer } from '../../../components/molecules/LegalDisclaimer/LegalDisclaimer';
import { Info } from '../../../components/atoms/Info/Info';
import { ProviderListHeader } from '../../components/ProvidersList/ProviderListHeader';
import {
  ProvidersList,
  SectionedProvidersList,
  SelectProviderViewSetGroup,
} from '../../components/ProvidersList/ProvidersList';
import { commonMessages } from '../../../components/atoms/CommonMessages/messages';
import { ProviderEntity } from '../../../models/provider';
import { useVersion } from '../../../components/ApplicationProviders/VersionProvider/VersionProvider';
import { usePageViewEvents as usePageViewEventsImport } from '../../Analytics/usePageViewEvents';

import { useCheckMultibankingSession as useCheckMultibankingSessionImport } from '../../../banking/components/multibanking/useCheckMultibankingSession';
import { applicationParameters } from '../../../utilities/application-parameters';
import { selectUser } from '../../Analytics/new-analytics';
import { useGradualRollout as useGradualRolloutImport } from '../../../utilities/useGradualRollout';
import { GradualReleaseLabels } from '../../../utilities/useGradualRolloutTypes';
import { messages as headerMessages } from '../../components/ProvidersList/messages';
import { useProduct } from '../../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { messages } from './messages';
import {
  ProviderViewSetArgs,
  useProviderSearch as useProviderSearchImport,
} from './useProviderSearch';

const shouldGroupByDisplayName = (searchString: string) => searchString.trim().length > 0;

const MIN_PROVIDERS_FOR_SECTIONED_LIST = 16;

export const searchProviderGroups = (
  { providers, version, providerSortingStrategy }: ProviderViewSetArgs,
  searchString: string
) => {
  if (shouldGroupByDisplayName(searchString)) {
    return searchProviders({
      providers,
      searchKeys: ['displayName', 'groupDisplayName'],
      searchString: searchString.trim(),
      version,
    });
  }

  return new ProviderViewSet(
    providers,
    version,
    undefined,
    undefined,
    providerSortingStrategy
  ).groupByGroupDisplayName();
};

export type ProviderGroupPickerProps = {
  providers: ProviderEntity[];
  showProviderMissingError: boolean;
  onProviderItemClick: SelectProviderViewSetGroup;
  onProviderViewSetClick: SelectProviderViewSetGroup;
  usePageViewEvents?: ({ pageView }: { pageView: PageView }) => void;
  useProviderSearch?: typeof useProviderSearchImport;
  useCheckMultibankingSession?: typeof useCheckMultibankingSessionImport;
  useGradualRollout?: typeof useGradualRolloutImport;
};

export const ProviderGroupPicker = ({
  providers,
  showProviderMissingError,
  onProviderItemClick,
  onProviderViewSetClick,
  usePageViewEvents = usePageViewEventsImport,
  useProviderSearch = useProviderSearchImport,
  useCheckMultibankingSession = useCheckMultibankingSessionImport,
  useGradualRollout = useGradualRolloutImport,
}: ProviderGroupPickerProps) => {
  const intl = useIntl();
  const {
    version: { version },
  } = useVersion();
  const market = applicationParameters.get('market');

  const { productType } = useProduct();

  const isMultiBankingEnabled = useCheckMultibankingSession();

  const [currentSearchQuery, setCurrentSearchQuery] = useState<string>('');

  const providerSortingStrategyToUse = getProviderSortingStrategy(market);

  const { providerViewSetGroups, onSearchChange, sendSearchHistoryAnalytic } = useProviderSearch({
    pageView: PageView.PROVIDER_SELECTION_SCREEN,
    providerViewSetArgs: {
      providers,
      version,
      providerSortingStrategy: providerSortingStrategyToUse,
    },
    filterFn: searchProviderGroups,
    delay: 275,
  });

  const providersToRender = limitNumberOfProviders({
    providerGroups: providerViewSetGroups,
    market,
    isSearching: currentSearchQuery.length > 0,
    providerSortingStrategy: providerSortingStrategyToUse,
  });

  const [isGroupedByDisplayName, setGroupedByDisplayName] = useState(false);
  const handleSearchChange = useCallback(
    (searchString: string) => {
      setCurrentSearchQuery(searchString);
      setGroupedByDisplayName(shouldGroupByDisplayName(searchString));
      onSearchChange(searchString);
    },
    [onSearchChange]
  );

  usePageViewEvents({
    pageView: PageView.PROVIDER_SELECTION_SCREEN,
  });

  const hasFewProviders = providersToRender.length < MIN_PROVIDERS_FOR_SECTIONED_LIST;
  const user = useSelector(selectUser);
  const { isInTestGroup } = useGradualRollout(
    PageView.PROVIDER_SELECTION_SCREEN,
    GradualReleaseLabels.DEFAULT_FLOWS_PROVIDER_LIST_50
  );

  const shouldDisplaySectionedProviderList = useMemo(() => {
    if (hasFewProviders) {
      return false;
    }

    return isInTestGroup({ userId: user?.id || '', market, productType });
  }, [hasFewProviders, isInTestGroup, user?.id, market, productType]);

  const inTestGroup = shouldDisplaySectionedProviderList;
  const ProviderListComponent = inTestGroup ? SectionedProvidersList : ProvidersList;

  return (
    <ScreenWrapper>
      <Header pageView={PageView.PROVIDER_SELECTION_SCREEN} onBack={undefined} />
      <ContentBody fullWidth>
        <ProviderListHeader
          placeholder={intl.formatMessage(commonMessages.search)}
          onSearchChange={handleSearchChange}
          isMultibanking={isMultiBankingEnabled}
          headerMessage={headerMessages.selectYourBank}
        />
        {showProviderMissingError && (
          <Info padding="normal" mb="normedium" data-testid="provider-missing-error">
            {intl.formatMessage(messages.providerDoesNotExist)}
          </Info>
        )}
        <ProviderListComponent
          currentSearchQuery={currentSearchQuery}
          onProviderItemClick={(payload) => {
            sendSearchHistoryAnalytic();

            if (isGroupedByDisplayName) {
              onProviderItemClick(payload);
            } else {
              onProviderViewSetClick(payload);
            }
          }}
          providerViewSetGroups={providersToRender}
        />
      </ContentBody>
      <LegalDisclaimer pageView={PageView.PROVIDER_SELECTION_SCREEN} />
    </ScreenWrapper>
  );
};
